/* eslint-disable import/prefer-default-export */
import { useEffect } from 'react';

interface UseStickyOffsetOptions {
  /** CSS variable name to set with the measured height */
  cssVarName?: string;
  /** Debounce delay in ms for resize events (default: 16ms ~60fps) */
  debounceMs?: number;
}

/**
 * Measures an element's height and exposes it as a CSS variable
 * for use in sticky positioning calculations.
 *
 * This hook solves the problem of hardcoded sticky offsets by dynamically
 * measuring element heights and making them available to CSS via custom properties.
 * Works across responsive breakpoints, font changes, zoom levels, and text wrapping.
 *
 * @param element - The element to measure (typically a sticky header)
 * @param options - Configuration options
 *
 * @example
 * ```tsx
 * const headerRef = useRef<HTMLElement>(null);
 * useStickyOffset(headerRef.current, {
 *   cssVarName: '--rules-dashboard-header-height'
 * });
 * ```
 *
 * Then in CSS:
 * ```css
 * .second-sticky {
 *   top: calc(var(--host-top-nav-height) + var(--rules-dashboard-header-height));
 * }
 * ```
 */
export function useStickyOffset(
  element: HTMLElement | null,
  options: UseStickyOffsetOptions = {}
) {
  const { cssVarName = '--sticky-element-height', debounceMs = 16 } = options;

  useEffect(() => {
    if (!element) {
      return () => {};
    }

    let timeoutId: number | null = null;

    // Update CSS variable with current element height
    const updateHeight = () => {
      // getBoundingClientRect() accounts for wrapping text, transforms, zoom, etc.
      const rect = element.getBoundingClientRect();
      const height = `${rect.height}px`;

      // Set CSS custom property on document root
      document.documentElement.style.setProperty(cssVarName, height);
    };

    // Debounced version for resize events to prevent excessive calls
    const debouncedUpdate = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = window.setTimeout(() => {
        updateHeight();
      }, debounceMs);
    };

    // ResizeObserver for efficient element size change detection
    const resizeObserver = new ResizeObserver(() => {
      updateHeight();
    });

    resizeObserver.observe(element);

    // Handle viewport changes that might affect layout
    // These are debounced since they can fire rapidly during resize/rotation
    window.addEventListener('resize', debouncedUpdate, { passive: true });
    window.addEventListener('orientationchange', debouncedUpdate, {
      passive: true,
    });

    updateHeight(); // set initial value on mount

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', debouncedUpdate);
      window.removeEventListener('orientationchange', debouncedUpdate);

      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [element, cssVarName, debounceMs]);
}
